import { useContext } from "react";
import AvatarWithName from "v2/common/AvatarWithName";
import { getSessionStore } from "app/utils/sessionStore";
import { ProposalTypes } from "app/utils/types";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";
import { Avatar } from "primereact/avatar";
import ServiceStackIcon from "app/assets/IconComponent/ServiceStackIcon";

interface PolicyRow {
  id: string;
  insuredName: string;
  insuredAvatar?: string;
  policyNumber: string;
  policyType?: string;
  policySubType?: string;
  accountValue: string;
  surrenderValue: string;
  benefit: string;
  ownerName: string;
  ownerLogo?: string;
  beneficiary: string;
  beneficiaryAvatar?: string;
  policyIcon: string;
  primaryColor?: string;
  primaryColorDark?: string;
  insuranceCompanyLogo?: string;
  insuranceCompanyLogoDark?: string;
  insuranceCompanyName: string;
}

type ColKey = keyof PolicyRow | "insurer";

// Premium Finance layout (account / surrender values).
const COLUMNS: { key: ColKey; label: string; width: string }[] = [
  { key: "insuredName", label: "Insured", width: "18%" },
  { key: "policyNumber", label: "Policy #", width: "15%" },
  { key: "accountValue", label: "Account Value", width: "13%" },
  { key: "surrenderValue", label: "Surrender Value", width: "13%" },
  { key: "benefit", label: "Benefit", width: "10%" },
  { key: "ownerName", label: "Owner", width: "18%" },
  { key: "beneficiary", label: "Beneficiary", width: "13%" },
];

// Buy Sell / Key Person layout (per Figma): Type, Insurer, Policy #, Benefit —
// no account / surrender values.
const PEOPLE_COLUMNS: { key: ColKey; label: string; width: string }[] = [
  { key: "insuredName", label: "Insured", width: "16%" },
  { key: "policyType", label: "Type (Sub-Type)", width: "14%" },
  { key: "insurer", label: "Insurer", width: "16%" },
  { key: "policyNumber", label: "Policy #", width: "13%" },
  { key: "benefit", label: "Benefit", width: "10%" },
  { key: "ownerName", label: "Owner", width: "16%" },
  { key: "beneficiary", label: "Beneficiary", width: "13%" },
];

export type { PolicyRow };

interface PolicyInformationCardProps {
  policies?: PolicyRow[];
}

const PolicyInformationCard = ({ policies }: PolicyInformationCardProps = {}) => {
  const { isDarkMode } = useContext(ThemeContext);
  const isDark = isDarkMode;
  const planType = getSessionStore("planInfo")?.planType;
  const isPeoplePlan =
    planType === ProposalTypes.BuySell || planType === ProposalTypes.KeyPersonInsurance;
  const activeColumns = isPeoplePlan ? PEOPLE_COLUMNS : COLUMNS;
  const rows = policies ?? [];

  const headerText = isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]";
  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-[#F5F5F5]";
  const thBg = isDark ? "bg-[#1E1E2E]" : "bg-white";
  const thText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const tdText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const divider = isDark ? "!border-[#2E2E42]" : "!border-[#F1F5F9]";

  const PolicyBadge = ({
    policyIcon,
    companyName,
    color,
  }: {
    policyIcon?: string;
    companyName: string;
    color: string;
  }) => (
    <div
      className="w-max rounded-full p-1 flex items-center justify-center"
      style={{ border: `2px solid ${color}` }}
    >
      <div className="w-full h-full rounded-full shadow-sm flex items-center justify-center">
        {policyIcon ? (
          <Avatar image={policyIcon} shape="circle" style={{ width: "32", height: "32" }} />
        ) : (
          <div
            className="w-[32px] h-[32px] rounded-full flex items-center justify-center text-white text-[13px] font-bold"
            style={{ backgroundColor: color }}
          >
            {companyName.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );

  const renderCell = (row: PolicyRow, col: (typeof COLUMNS)[number]) => {
    const brandColor = (isDark ? row.primaryColorDark : row.primaryColor) ?? "#22C55E";
    // Prefer the dark branding logo in dark mode; fall back to the light logo.
    const companyLogo =
      (isDark ? row.insuranceCompanyLogoDark : row.insuranceCompanyLogo) ??
      row.insuranceCompanyLogo;

    switch (col.key) {
      case "insuredName":
        return (
          <AvatarWithName
            imageUrl={row.insuredAvatar}
            name={row.insuredName}
            tooltipText={row.insuredName.length > 20 ? row.insuredName : undefined}
            avatarStyle={{ border: "1px solid #cbd5e1" }}
            nameClassName={`font-medium text-[14px] ${tdText}`}
          />
        );

      case "policyType":
        return (
          <span className={`text-[14px] ${tdText}`}>
            {row.policyType || "—"}
            {row.policySubType ? ` (${row.policySubType})` : ""}
          </span>
        );

      case "insurer":
        return (
          <div className="flex items-center gap-2">
            <PolicyBadge
              policyIcon={companyLogo}
              companyName={row.insuranceCompanyName}
              color={brandColor}
            />
            <span className={`text-[14px] ${tdText}`}>{row.insuranceCompanyName}</span>
          </div>
        );

      case "policyNumber":
        return (
          <div className="flex items-center gap-2">
            {/* People plans show the insurer in its own column, so skip the badge here. */}
            {!isPeoplePlan && (
              <PolicyBadge
                policyIcon={companyLogo}
                companyName={row.insuranceCompanyName}
                color={brandColor}
              />
            )}
            <div className="flex items-center gap-1">
              <span className="w-[12px] h-[12px] rounded-full bg-[#22C55E] flex-shrink-1" />
              <span className={`text-[14px] font-semibold ${tdText}`}>{row.policyNumber}</span>
            </div>
          </div>
        );

      case "accountValue":
      case "surrenderValue":
      case "benefit":
        return <span className={`text-[14px] font-bold ${tdText}`}>{row[col.key]}</span>;

      case "ownerName":
        return (
          <AvatarWithName
            imageUrl={row.ownerLogo}
            name={row.ownerName}
            tooltipText={row?.ownerName?.length > 20 ? row.ownerName : undefined}
            avatarStyle={{ border: "1px solid #cbd5e1" }}
            nameClassName={`font-medium text-[14px] ${tdText}`}
          />
        );

      case "beneficiary":
        // People plans show an avatar + name; other plans keep plain text.
        return isPeoplePlan ? (
          <AvatarWithName
            imageUrl={row.beneficiaryAvatar}
            name={row.beneficiary}
            tooltipText={row.beneficiary?.length > 20 ? row.beneficiary : undefined}
            avatarStyle={{ border: "1px solid #cbd5e1" }}
            nameClassName="font-medium text-[14px]"
          />
        ) : (
          <span className={`text-[14px] ${tdText}`}>{row.beneficiary}</span>
        );

      default:
        return (
          <span className={`text-[14px] ${tdText}`}>
            {row[col.key as keyof PolicyRow] as string}
          </span>
        );
    }
  };

  return (
    <div className={`w-full rounded-lg shadow-lg overflow-hidden mt-4 ${cardBg}`}>
      <p
        className={`text-[13px] font-semibold conthrax-font p-4  flex items-center ${cardBg} gap-2  ${headerText}`}
      >
        <ServiceStackIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} width="18px" />
        Policy Information
      </p>

      {/* ── Table ── */}
      <div className="w-full overflow-x-auto rounded-b-lg ">
        <table className="w-full border-collapse">
          <thead className="border-b-2 border-[#F5F5F5]">
            <tr className={`${thBg}`}>
              {activeColumns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={`px-4 py-3 text-left text-[14px] font-medium uppercase whitespace-nowrap tracking-wider ${thText} border-b ${divider}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={isDark ? "" : "bg-white"}>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={activeColumns.length}
                  className={`px-4 py-6 text-center text-[14px] ${thText} `}
                >
                  No Data Found
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`transition-colors ${
                    isDark ? "hover:bg-[#1E1E2E]" : "hover:bg-[#F8FAFC]"
                  } ${index < rows.length - 1 ? `border-b ${divider}` : ""}`}
                >
                  {activeColumns.map((col) => (
                    <td key={col.key} className={`px-4 py-4 ${isDark ? "2A2A3D" : "!bg-white"}`}>
                      {renderCell(row, col)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PolicyInformationCard;
